import type { AsyncResourceVariant } from "../../index.js";
import {
  ListDataModel,
  ListQueryModel,
  DataModel,
  AggregateMetaData,
  ReferenceModel,
} from "../../base";
import { provideReact } from "../../react/index.js";
import { DateTime } from "luxon";
import { classes } from "polytype";
import type { LeadFilterType } from "../../../store/fyndr";
import assertObjectFound from "../../base/assertObjectFound";
import { config } from "../../config/config";
import { LOCATION_DACH_ZIP_CODE } from "../City";
import { getFormattedSalesVolume } from "../util/helper";
import type {
  LeadCompanyData,
  LeadData,
  LeadListItemData,
  LeadListQueryData,
  LeadMetricsData,
  LeadTechnologyData,
} from "./types";

export class Lead extends ReferenceModel {
  public static aggregateMetaData = new AggregateMetaData("leadfinder", "lead");

  private readonly customerId: string;

  public constructor(customerId: string, id: string) {
    super(id);
    this.customerId = customerId;
  }

  public static ofId(customerId: string, leadId: string) {
    return new Lead(customerId, leadId);
  }

  public static find = provideReact(async (customerId: string, id: string) => {
    const data = await config.behaviors.lead.find(customerId, id);
    if (data) {
      return new LeadDetailed(customerId, data);
    }
  });

  public static get = provideReact(async (customerId: string, id: string) => {
    const lead = await this.find(customerId, id);
    assertObjectFound(lead, "lead", id);
    return lead;
  });

  public findDetailed = provideReact(
    () => Lead.find(this.customerId, this.id),
    [this.id],
  ) as AsyncResourceVariant<() => Promise<LeadDetailed | undefined>>;

  public getDetailed = provideReact(
    () => Lead.get(this.customerId, this.id),
    [this.id],
  ) as AsyncResourceVariant<() => Promise<LeadDetailed>>;

  public static query(customerId: string, query: LeadListQueryData = {}) {
    return new LeadListQuery(customerId, query);
  }

  public static queryFromStoreFilters(
    customerId: string,
    filterOptions: LeadFilterType,
  ) {
    const renamedTechnologies = filterOptions?.technologies.map((value) => {
      switch (value) {
        case "TYPO3":
          return "TYPO3 CMS";
        default:
          return value;
      }
    });

    const builtLocationFilter =
      filterOptions?.location &&
      filterOptions.location.zipCode !== LOCATION_DACH_ZIP_CODE
        ? {
            locationCity: filterOptions.location.city,
            locationPostCode: filterOptions.location.zipCode,
            locationRadiusInKm: Number.parseInt(filterOptions.location.radius),
          }
        : undefined;

    return new LeadListQuery(customerId, {
      businessFields: filterOptions?.businessFields,
      technologies: renamedTechnologies,
      ...builtLocationFilter,
      employeeCountMin: filterOptions?.employeeCount?.min
        ? Number.parseInt(filterOptions.employeeCount.min)
        : undefined,
      employeeCountMax: filterOptions?.employeeCount?.max
        ? Number.parseInt(filterOptions.employeeCount.max)
        : undefined,
    });
  }

  public async unlock() {
    await config.behaviors.lead.unlock(this.customerId, this.id);
  }
}

class LeadCommon extends classes(DataModel<LeadData | LeadListItemData>, Lead) {
  public readonly businessFields: string;
  public readonly description: string;
  public readonly screenshotBase64: string;
  public readonly potential: number;
  public readonly metrics: LeadMetricsData;
  public readonly mainTechnology: LeadTechnologyData | undefined;
  public readonly company: LeadCompanyData;
  public readonly technologies: LeadTechnologyData[];
  public readonly scannedAt: DateTime | undefined;

  public constructor(customerId: string, data: LeadData | LeadListItemData) {
    super([data], [customerId, data.leadId]);
    this.businessFields = data.businessFields.join(", ");
    this.description = data.description;
    this.screenshotBase64 = data.screenshot;
    this.potential = Math.round(data.potential * 100);
    this.metrics = data.metrics;
    this.mainTechnology = data.mainTechnology;
    this.company = data.company;
    this.technologies = data.technologies;
    this.scannedAt = data.scannedAt
      ? DateTime.fromISO(data.scannedAt)
      : undefined;
  }

  public get formattedSalesVolume(): string {
    return getFormattedSalesVolume(this.company.salesVolume);
  }

  public get potentialType(): "high" | "medium" | "low" {
    if (this.potential > 60) {
      return "high";
    } else if (this.potential > 50) {
      return "medium";
    } else {
      return "low";
    }
  }
}

export class LeadDetailed extends classes(LeadCommon, DataModel<LeadData>) {
  public constructor(customerId: string, data: LeadData) {
    super([customerId, data], [data]);
  }
}

export class LeadListItem extends classes(
  LeadCommon,
  DataModel<LeadListItemData>,
) {
  public constructor(customerId: string, data: LeadListItemData) {
    super([customerId, data], [data]);
  }
}

export class LeadListQuery extends ListQueryModel<LeadListQueryData> {
  private readonly customerId: string;

  public constructor(customerId: string, query: LeadListQueryData = {}) {
    super(query);
    this.customerId = customerId;
  }

  public refine(customerId: string, query: LeadListQueryData) {
    return new LeadListQuery(customerId, {
      ...this.query,
      ...query,
    });
  }

  public execute = provideReact(async () => {
    const { items, totalCount } = await config.behaviors.lead.list(
      this.customerId,
      this.query,
    );

    return new LeadList(
      this.customerId,
      this.query,
      items.map((l) => new LeadListItem(this.customerId, l)),
      totalCount,
    );
  }, [this.queryId, "lead"]);

  public getTotalCount = provideReact(async () => {
    const { totalCount } = await this.refine(this.customerId, {
      limit: 1,
    }).execute();
    return totalCount;
  }, [this.queryId, "lead"]);
}

export class LeadList extends classes(
  LeadListQuery,
  ListDataModel<LeadListItem>,
) {
  public constructor(
    customerId: string,
    query: LeadListQueryData,
    leads: LeadListItem[],
    totalCount: number,
  ) {
    super([customerId, query], [leads, totalCount]);
  }
}
